import { computed, ref } from "vue";
import { defineStore } from "pinia";
import { FEATURE_FLAG_DEFAULTS, } from "@/constants/feature-flags";
import { featureFlagRepository } from "../repositories/feature-flag.repository";
const featureFlagDefaults = FEATURE_FLAG_DEFAULTS;
function defaultSnapshot() {
    return { ...featureFlagDefaults };
}
function resolveFlag(source, flagKey) {
    return source[flagKey] ?? featureFlagDefaults[flagKey] ?? false;
}
const CACHE_DURATION = 5 * 60 * 1000;
export const useFeatureFlagStore = defineStore("dbFeatureFlags", () => {
    const runtimeFlags = ref(defaultSnapshot());
    const globalFlags = ref(defaultSnapshot());
    const runtimeLoadedForOrgId = ref(null);
    const globalLoaded = ref(false);
    const isLoadingRuntime = ref(false);
    const isLoadingGlobal = ref(false);
    const runtimeError = ref(null);
    const globalError = ref(null);
    const runtimeLastFetched = ref(null);
    const globalLastFetched = ref(null);
    const isRuntimeCacheValid = computed(() => {
        if (!runtimeLastFetched.value)
            return false;
        return Date.now() - runtimeLastFetched.value.getTime() < CACHE_DURATION;
    });
    const isGlobalCacheValid = computed(() => {
        if (!globalLastFetched.value)
            return false;
        return Date.now() - globalLastFetched.value.getTime() < CACHE_DURATION;
    });
    const runtimeSnapshot = computed(() => runtimeFlags.value);
    const globalSnapshot = computed(() => globalFlags.value);
    function resetRuntime() {
        runtimeFlags.value = defaultSnapshot();
        runtimeLoadedForOrgId.value = null;
        runtimeError.value = null;
        runtimeLastFetched.value = null;
    }
    async function loadRuntime(orgId, options = {}) {
        const normalizedOrgId = orgId == null ? null : String(orgId);
        if (!normalizedOrgId) {
            resetRuntime();
            return;
        }
        if (!options.force &&
            runtimeLoadedForOrgId.value === normalizedOrgId &&
            isRuntimeCacheValid.value &&
            !runtimeError.value) {
            return;
        }
        isLoadingRuntime.value = true;
        runtimeError.value = null;
        try {
            runtimeFlags.value = {
                ...defaultSnapshot(),
                ...(await featureFlagRepository.getRuntime()),
            };
            runtimeLoadedForOrgId.value = normalizedOrgId;
            runtimeLastFetched.value = new Date();
        }
        catch (err) {
            runtimeFlags.value = defaultSnapshot();
            runtimeError.value =
                err instanceof Error ? err.message : "Could not load feature flags";
        }
        finally {
            isLoadingRuntime.value = false;
        }
    }
    async function loadGlobal(options = {}) {
        if (!options.force &&
            globalLoaded.value &&
            isGlobalCacheValid.value &&
            !globalError.value) {
            return;
        }
        isLoadingGlobal.value = true;
        globalError.value = null;
        try {
            const flags = await featureFlagRepository.getAdminFlags();
            globalFlags.value = flags.reduce((acc, flag) => {
                acc[flag.key] = Boolean(flag.globalValue);
                return acc;
            }, defaultSnapshot());
            globalLoaded.value = true;
            globalLastFetched.value = new Date();
        }
        catch (err) {
            globalFlags.value = defaultSnapshot();
            globalError.value =
                err instanceof Error ? err.message : "Could not load feature flags";
        }
        finally {
            isLoadingGlobal.value = false;
        }
    }
    async function setGlobalValue(flagId, enabled) {
        await featureFlagRepository.updateGlobalValue(flagId, enabled);
        await loadGlobal({ force: true });
        if (runtimeLoadedForOrgId.value !== null) {
            await loadRuntime(runtimeLoadedForOrgId.value, { force: true });
        }
    }
    async function setOrgOverride(flagId, organizationId, enabled) {
        await featureFlagRepository.upsertOrgOverride(flagId, organizationId, enabled);
        if (runtimeLoadedForOrgId.value !== null &&
            String(organizationId) === runtimeLoadedForOrgId.value) {
            await loadRuntime(runtimeLoadedForOrgId.value, { force: true });
        }
    }
    function isRuntimeEnabled(flagKey) {
        return resolveFlag(runtimeFlags.value, flagKey);
    }
    function isGlobalEnabled(flagKey) {
        return resolveFlag(globalFlags.value, flagKey);
    }
    function $reset() {
        runtimeFlags.value = defaultSnapshot();
        globalFlags.value = defaultSnapshot();
        runtimeLoadedForOrgId.value = null;
        globalLoaded.value = false;
        isLoadingRuntime.value = false;
        isLoadingGlobal.value = false;
        runtimeError.value = null;
        globalError.value = null;
        runtimeLastFetched.value = null;
        globalLastFetched.value = null;
    }
    return {
        runtimeFlags,
        globalFlags,
        runtimeLoadedForOrgId,
        globalLoaded,
        isLoadingRuntime,
        isLoadingGlobal,
        runtimeError,
        globalError,
        runtimeLastFetched,
        globalLastFetched,
        isRuntimeCacheValid,
        isGlobalCacheValid,
        runtimeSnapshot,
        globalSnapshot,
        resetRuntime,
        loadRuntime,
        loadGlobal,
        setGlobalValue,
        setOrgOverride,
        isRuntimeEnabled,
        isGlobalEnabled,
        $reset,
    };
});
